import React from 'react';
import { format } from 'date-fns';
import { connect } from 'react-redux';
import { isEmpty, isNil } from 'ramda';
import { func, object, string } from 'prop-types';

import { SvgIcon } from '../svg-icon';
import { modalNames } from '../../modals';
import { thousandSeparator } from '../../utils';
import { USER_ROLES } from '../../api/constants';
import { Popconfirm, Tooltip } from '../popovers';
import { openModal } from '../../redux/actions/modal';

import {
  LogWrap,
  LogInfo,
  TextWrap,
  CreatorDataWrap,
  CreatorName,
  Date,
  Status,
  ChangeLogInfo,
  LogText,
  LogTextAttachmentFile,
  NewItem,
  TaskItem,
  Label,
  DeleteIconWrapper,
} from './styles';

const DATE_FORMAT = 'HH:mm d MMM yyyy';

const Creator = ({ fullName, date }) => (
  <CreatorDataWrap>
    <CreatorName>{fullName}</CreatorName>
    <Date>{format(date, DATE_FORMAT)}</Date>
  </CreatorDataWrap>
);

Creator.propTypes = {
  fullName: string.isRequired,
  date: string.isRequired,
};

const Log = ({
  _id,
  createdBy,
  createdAt,
  payload,
  openTaskModal,
  openCommentModal,
  authorizedUser,
  requestId,
  requests,
  handleLogDelete,
}) => {
  const {
    initialStatus,
    changedFrom,
    changedTo,
    oldBudget,
    newBudget,
    newComment,
    newTasks,
    newAttachments,
    addedLabels,
    removedLabels,
  } = payload;
  const currentRequest = requests.items[requestId];
  const isDeletingAvailable = authorizedUser.role === USER_ROLES.SUPER_ADMIN;
  return (
    <LogWrap withPadding={isDeletingAvailable}>
      {initialStatus &&
      <TextWrap>
        Request was created with
        {' '}
        <Status color={initialStatus.color}>
          {payload && initialStatus && initialStatus.title}
        </Status>
        {' '}
        status
      </TextWrap>
      }
      {changedFrom && changedTo &&
      <React.Fragment>
        <TextWrap>
          Request status was changed from
          {' '}
          <Status color={changedFrom.color}>
            {payload && changedFrom && changedFrom.title}
          </Status>
          {' '} to {' '}
          <Status color={changedTo.color}>
            {payload && changedTo && changedTo.title}
          </Status>
        </TextWrap>

        {(oldBudget || newComment || newTasks || addedLabels || removedLabels || newAttachments) &&
        <ChangeLogInfo>
          <ul>

            {(oldBudget && !(isEmpty(oldBudget) && !isEmpty(newBudget))) ?
              <li>
                <LogText bold>
                  Budget was changed from
                  {` $${thousandSeparator(oldBudget[0])} `}
                  {oldBudget[1] && `- $${thousandSeparator(oldBudget[1])} `}
                  to
                  {` $${thousandSeparator(newBudget[0])} `}
                  {newBudget[1] && `- $${thousandSeparator(newBudget[1])} `}
                </LogText>
              </li>
              :
              null
            }

            {(isEmpty(oldBudget) && !isEmpty(newBudget)) &&
              <li>
                <LogText bold>
              Budget was added
                  {` $${thousandSeparator(newBudget[0])} `}
                  {newBudget[1] && `- $${thousandSeparator(newBudget[1])} `}
                </LogText>
              </li>
            }

            {newComment &&
              <li>
                <LogText
                  bold
                  active
                  goldText
                  itemIsDeleted={!currentRequest.comments.includes(newComment)}
                  onClick={() => currentRequest.comments.includes(newComment)
                    ? openCommentModal(requestId, newComment, true)
                    : null}
                >
                  <Tooltip
                    placement="bottomLeft"
                    title={currentRequest.comments.includes(newComment)
                      ? 'View more...'
                      : 'Comment was deleted'}
                  >
                    New comment
                  </Tooltip>
                </LogText>
              </li>
            }

            {newTasks &&
            <li>
              <LogText bold >
                New task{newTasks.length > 1 ? 's' : null}:
              </LogText>
              <ul className="child-list">
                {newTasks.map((task) => {
                  const hasTask = currentRequest.tasks.filter(taskId =>
                    taskId === task._id).length;
                  return (<NewItem
                    key={task._id}
                  >
                    <TaskItem
                      key={task._id}
                      taskIsDeleted={!hasTask}
                      onClick={() => hasTask
                        ? openTaskModal(task._id, requestId)
                        : null}
                    >
                      <Tooltip
                        placement="bottomLeft"
                        title={hasTask ? 'View more...' : 'Task was deleted'}
                      >
                        {task.title}
                      </Tooltip>
                    </TaskItem>
                  </NewItem>);
                })}
              </ul>
            </li>
            }

            {(!isNil(removedLabels) && !isEmpty(removedLabels)) &&
            <li>
              <LogText bold >
                Removed label{removedLabels.length > 1 ? 's' : null}:
              </LogText>
              <ul className="child-list">
                {removedLabels.map(label =>
                  (<NewItem
                    key={label._id}
                  >
                    <Label
                      key={label._id}
                      bgColor={label.color}
                    >
                      {label.title}
                    </Label>
                  </NewItem>))}
              </ul>
            </li>
            }

            {(!isNil(addedLabels) && !isEmpty(addedLabels)) &&
            <li>
              <LogText bold >
                Added label{addedLabels.length > 1 ? 's' : null}:
              </LogText>
              <ul className="child-list">
                {addedLabels.map(label =>
                  (<NewItem
                    key={label._id}
                  >
                    <Label
                      key={label._id}
                      bgColor={label.color}
                    >
                      {label.title}
                    </Label>
                  </NewItem>))}
              </ul>
            </li>
            }

            {newAttachments ?
              <li>
                <LogText bold >
                  New file{newAttachments.length > 1 ? 's' : null}:
                </LogText>
                <ul className="child-list">
                  <LogTextAttachmentFile>
                    {newAttachments
                      .map(attachment => (
                        <NewItem key={attachment._id}>
                          <a
                            disabled={!attachment.url}
                            href={attachment.url}
                            download={attachment.fileName}
                            target="__blank"
                            title={attachment.fileName}
                          > {attachment.fileName}
                          </a>
                        </NewItem>
                      ))}
                  </LogTextAttachmentFile>
                </ul>
              </li>
              :
              null}

          </ul>
        </ChangeLogInfo>
        }
      </React.Fragment>
      }
      <LogInfo>
        <Creator
          fullName={createdBy.fullName}
          date={createdAt}
        />
      </LogInfo>
      {isDeletingAvailable && (
        <DeleteIconWrapper>
          <Popconfirm
            title="Are you sure to delete this log?"
            onConfirm={() => handleLogDelete(_id)}
          >
            <SvgIcon
              type="trash"
              fillOpacity={0.5}
              data-popconfirm-stay-open
            />
          </Popconfirm>
        </DeleteIconWrapper>
      )}
    </LogWrap>
  );
};

const mapDispatchToProps = dispatch => ({
  openTaskModal: (taskId, requestId) =>
    dispatch(openModal({
      name: modalNames.TASKS,
      payload: {
        taskId, requestId, type: 'view',
      },
    })),
  openCommentModal: (requestId, commentId, readOnly) => {
    dispatch(openModal({
      name: modalNames.COMMENTS,
      payload: { requestId, commentId, readOnly },
    }));
  },
});

const mapStateToProps = ({ modal, requests }) => ({
  modal,
  requests,
});

Log.propTypes = {
  _id: string.isRequired,
  payload: object.isRequired,
  requests: object.isRequired,
  createdAt: string.isRequired,
  createdBy: object.isRequired,
  requestId: string.isRequired,
  openTaskModal: func.isRequired,
  handleLogDelete: func.isRequired,
  openCommentModal: func.isRequired,
  authorizedUser: object.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(Log);



// WEBPACK FOOTER //
// ./src/components/logs-simple-list/list-item.js